import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  HStack,
  ListItem,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import {
  CloudDownload as CloudDownloadIcon,
  Folder as FolderIcon,
} from "@mui/icons-material";
import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import {
  GhostButton,
  SymbolOutlineButton,
} from "~/src/components/common/Button";
import { FormControl } from "~/src/components/common/forms/FormControl";
import { FormControlGroup } from "~/src/components/common/forms/FormControlGroup";
import { Modal } from "~/src/components/common/Modal";
import { Switch } from "~/src/components/common/Switch";
import { chakraFactory } from "~/src/lib/chakra-ui";

import {
  IMPORT_MAX_POPUPS,
  downloadImportTemplate,
  parseCsvToFormValues,
} from "../csvUtils";

export type Props = {
  isOpen: boolean;
  onClose: () => void;
  siteSlug: string;
};

const StyledSection = chakraFactory(Box, {
  baseStyle: {
    mb: "24px",
  },
});

const StyledFileName = chakraFactory(Text, {
  baseStyle: {
    fontSize: "13px",
    color: "#4A5568",
    ml: "8px",
  },
});

const StyledLimitText = chakraFactory(Text, {
  baseStyle: {
    fontSize: "12px",
    color: "#718096",
    mt: "4px",
  },
});

export const PopupCsvUploadModal: FC<Props> = ({
  isOpen,
  onClose,
  siteSlug,
}) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState("");
  const [csvErrors, setCsvErrors] = useState<string[]>([]);
  const [forceStatusInactive, setForceStatusInactive] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setCsvErrors([]);
    setFileName("");
    setForceStatusInactive(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [isOpen]);

  const handleTemplateDownload = useCallback(() => {
    downloadImportTemplate();
  }, []);

  const handleClickFileInput = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleForceStatusInactiveChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setForceStatusInactive(e.target.checked);
    },
    []
  );

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setFileName(file.name);
      setCsvErrors([]);

      const {
        rows,
        errors: parseErrors,
        csvMaxImageSlot,
      } = await parseCsvToFormValues(file, {
        forceStatusInactive,
      });
      if (parseErrors.length > 0) {
        setCsvErrors(parseErrors);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      navigate(`/sites/${siteSlug}/popup_groups/import`, {
        state: { popups: rows, csvMaxImageSlot },
      });
      onClose();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [forceStatusInactive, navigate, onClose, siteSlug]
  );

  return (
    <Modal title="CSVアップロード" isOpen={isOpen} onClose={onClose}>
      <StyledSection>
        <FormControlGroup>
          <FormControl as="div" label="テンプレート">
            <SymbolOutlineButton
              icon={<CloudDownloadIcon />}
              onClick={handleTemplateDownload}
            >
              CSVテンプレートをダウンロード
            </SymbolOutlineButton>
            <Text fontSize="11px" color="#718096" mt="4px">
              先頭に「アクション」列があります。続く列はポップアップ＆パターン登録状況一覧のエクスポートと同じ順序です。アップロードするCSVは、アクション列あり／なしのどちらでも利用できます。
            </Text>
          </FormControl>
          <FormControl as="div" label="CSVファイル">
            <HStack alignItems="center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                // eslint-disable-next-line react/forbid-dom-props
                style={{ display: "none" }}
              />
              <SymbolOutlineButton
                icon={<FolderIcon />}
                onClick={handleClickFileInput}
              >
                ファイルを選択
              </SymbolOutlineButton>
              {fileName ? <StyledFileName>{fileName}</StyledFileName> : null}
            </HStack>
            <StyledLimitText>
              ※ 最大{IMPORT_MAX_POPUPS}件まで一括インポートできます
            </StyledLimitText>
          </FormControl>
          <FormControl as="div" label="ステータス">
            <HStack alignItems="center" spacing="12px">
              <Switch
                isChecked={forceStatusInactive}
                onChange={handleForceStatusInactiveChange}
              />
              <Text fontSize="13px" color="#4A5568">
                アップロード時にすべての行のステータスをオフ（無効）にする
              </Text>
            </HStack>
          </FormControl>
        </FormControlGroup>
      </StyledSection>

      <Alert status="info" variant="left-accent" mb="16px" borderRadius="4px">
        <Box>
          <AlertTitle fontSize="14px" mb="8px">
            インポート時の注意
          </AlertTitle>
          <AlertDescription fontSize="13px">
            <UnorderedList spacing="6px" pl="4px" ml="12px">
              <ListItem>
                「アクション」列は任意です（テンプレートでは先頭列として含まれます）。一覧エクスポートのように列がないCSVでもアップロードできます。編集画面では未選択のとき「（未選択）」と表示され、保存前に登録または編集を選んでください。列がある場合は「登録」「編集」やそれと同じ意味の別表記が使え、セルが空欄のときの扱いも同様です。
              </ListItem>
              <ListItem>
                ファネル／実施ページに関する列は、対象のページやファネルがサイトに登録済みであることを確認してください。
              </ListItem>
              <ListItem>
                「登録」は新規作成です。「編集」を選ぶ場合はポップアップIDが必要です。
              </ListItem>
              <ListItem>
                画像は最大8スロットまで。CSVで指定できる画像URLはリモートURL（https://で始まる絶対URL）のみです。確認・編集画面では画像ファイルの差し替えにも対応しています。
              </ListItem>
              <ListItem>
                「登録」で同じポップアップグループ名（同一ページ）を複数行指定した場合、すべて同じポップアップグループにまとめて登録されます。
              </ListItem>
              <ListItem>
                上記のステータススイッチをオンにすると、アップロード時点で全行のステータスを無効にします（編集画面で確認・変更できます）。
              </ListItem>
            </UnorderedList>
          </AlertDescription>
        </Box>
      </Alert>

      {csvErrors.length > 0 ? (
        <Alert
          status="error"
          variant="left-accent"
          mb="16px"
          borderRadius="4px"
        >
          <VStack alignItems="flex-start" spacing="4px">
            {csvErrors.map((err, i) => (
              <AlertDescription key={i} fontSize="13px">
                {err}
              </AlertDescription>
            ))}
          </VStack>
        </Alert>
      ) : null}

      <HStack spacing="8px" justifyContent="flex-end">
        <GhostButton onClick={onClose}>閉じる</GhostButton>
      </HStack>
    </Modal>
  );
};
